import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import LoadingSpinner from '../ui/LoadingSpinner';

const RoleGuard = ({ children, requiredRole, fallbackPath = null }) => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check both active_role and roles array for multi-role support
  const currentRole = user?.active_role || user?.role;
  const userRoles = user?.roles || [user?.role].filter(Boolean);
  
  // Admin users should get access to all roles (creator, reviewer, approver)
  const hasRequiredRole = userRoles.includes(requiredRole) || 
                         (userRoles.includes('admin') && ['creator', 'reviewer', 'approver'].includes(requiredRole));
  
  // For role access, admin role can access any role's routes
  const isCurrentlyInRole = currentRole === requiredRole || 
                           (currentRole === 'admin' && ['creator', 'reviewer', 'approver'].includes(requiredRole));
  
  // User must both have the role and be currently active in that role
  if (!hasRequiredRole || !isCurrentlyInRole) {
    // If no specific fallback path, redirect to user's appropriate dashboard
    if (!fallbackPath) {
      switch (currentRole) {
        case 'admin':
          fallbackPath = '/super-admin';
          break;
        case 'creator':
          fallbackPath = '/admin';
          break;
        case 'reviewer':
          fallbackPath = '/reviewer';
          break;
        case 'approver':
          fallbackPath = '/approver';
          break;
        case 'candidate':
        default:
          fallbackPath = '/dashboard';
          break;
      }
    }
    return <Navigate to={fallbackPath} replace />;
  }

  return children;
};

export default RoleGuard;